import React from "react";
import CssBaseline from "@mui/material/CssBaseline";
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from "@mui/material/styles";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { outlinedInputClasses } from "@mui/material/OutlinedInput";

// To use a custom color for the color prop of a component, you must add it to the component's
// PropsColorOverrides interface. The example below shows how to do this with a Button component:
// <Button color="myAwesomeColor" />

// declare module "@mui/material/Button" {
//   interface ButtonPropsColorOverrides {
//     myAwesomeColor: true;
//   }
// }

// declare module "@mui/material/styles" {
//   interface Palette {
//     ochre: Palette["primary"];
//   }
//   // allow configuration using `createTheme`
//   interface PaletteOptions {
//     ochre?: PaletteOptions["primary"];
//   }
// }

declare module "@mui/material/styles" {
  interface Palette {
    surface: {
      accent: string;
      lighter: string;
      light: string;
      bg: string;
      bgNuanced: string;
      bgNuanced2: string;
      dark: string;
      darker: string;
      highlighted: string;
      selected: string;
      navigation: string;
      highlightedLighter: string;
    };
    status: {
      status1: string;
      status2: string;
      status3: string;
      status4: string;
      status5: string;
      status6: string;
      status7: string;
      status8: string;
      success: string;
      caution: string;
      error: string;
    };
    strokes: {
      defaultGrey: string;
      focused: string;
      lighter: string;
      darker: string;
    };
  }
  // allow configuration using `createTheme`
  interface PaletteOptions {
    surface?: {
      accent: string;
      lighter: string;
      light: string;
      bg: string;
      bgNuanced: string;
      bgNuanced2: string;
      dark: string;
      darker: string;
      highlighted: string;
      selected: string;
      navigation: string;
      highlightedLighter: string;
    };
    status?: {
      status1: string;
      status2: string;
      status3: string;
      status4: string;
      status5: string;
      status6: string;
      status7: string;
      status8: string;
      success: string;
      caution: string;
      error: string;
    };
    strokes?: {
      defaultGrey: string;
      focused: string;
      lighter: string;
      darker: string;
    };
  }

  interface PaletteTextChannel {
    accent?: string;
  }

  interface TypeText {
    accent?: string;
  }
}

const theme = createTheme({
  colorSchemes: {
    light: {
      palette: {
        primary: { main: "#00b373", contrastText: "#FFFFFF" },
        secondary: { main: "#0b3f30", contrastText: "#FFFFFF" },
        success: { main: "#12A96C", contrastText: "#FFFFFF" },
        warning: { main: "#ED9E27", contrastText: "#FFFFFF" },
        error: { main: "#E93F3F", contrastText: "#FFFFFF" },
        surface: {
          accent: "#0B3F30",
          lighter: "#FFFFFF",
          light: "#FFFFFF",
          bg: "#F4F4F8",
          bgNuanced: "#EDEDED",
          bgNuanced2: "#f6f6f6",
          dark: "#171717",
          darker: "#E6E8EB",
          highlighted: "#D9EBE6",
          selected: "#DBF3EE",
          navigation: "#FFFFFF",
          highlightedLighter: "#AED6CB",
        },
        text: {
          primary: "#171717",
          secondary: "#555555",
          accent: "#0B3F30",
        },
        status: {
          status1: "#2D6BE2", //mėlyna
          status2: "#AE52E7", //violetinė
          status3: "#ED9E27", //oranžinė
          status4: "#12A96C", //žalia
          status5: "#00CED1", //turkio
          status6: "#AE52E7", //violetinė
          status7: "#808000", //alyvuogių
          status8: "#FF7F50", //oranzinė
          success: "#12A96C",
          caution: "#ED9E27",
          error: "#E93F3F",
        },
        strokes: {
          defaultGrey: "#E0E0E0",
          focused: "00B373",
          lighter: "#FFFFFF",
          darker: "#FFFFFF",
        },
      },
    },
    dark: {
      palette: {
        primary: { main: "#00b373", contrastText: "#FFFFFF" },
        success: { main: "#B1E2C5" },
        warning: { main: "#FFDAA4" },
        error: { main: "#F04E4E" },
        surface: {
          accent: "#161617",
          lighter: "#28282A",
          light: "#1C1C1D",
          bg: "#161617",
          bgNuanced: "#28282A",
          bgNuanced2: "#353538",
          dark: "#181818",
          darker: "#0F0F10",
          highlighted: "#1E2622",
          selected: "#20302C",
          navigation: "#161617",
          highlightedLighter: "#30413E",
        },
        text: {
          primary: "#EFEFEF",
          secondary: "#A1A1A1",
          accent: "#00B373",
        },
        status: {
          status1: "#2D6BE2",
          status2: "#AE52E7",
          status3: "#D6AA1C",
          status4: "#1D9F7A",
          status5: "#00CED1",
          // status6: "#FF7F50",
          status6: "#AE52E7",
          status7: "#808000",
          status8: "#FF7F50",
          success: "#B1E2C5",
          caution: "#FFDAA4",
          error: "#F04E4E",
        },
        strokes: {
          defaultGrey: "#5D5D62",
          focused: "#00B373",
          lighter: "#FFFFFF",
          darker: "#FFFFFF",
        },
      },
    },
  },
  components: {
    // OnFocus always main brand color
    MuiTextField: {
      styleOverrides: {
        root: {
          "--TextField-brandBorderHoverColor": "#00b373",
          "--TextField-brandBorderFocusedColor": "#00b373",
          "& label.Mui-focused": {
            color: "var(--TextField-brandBorderFocusedColor)",
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => {
          const autofillTextColor = `${theme.palette.text.primary} !important`;

          return {
            [`& .${outlinedInputClasses.input}:-webkit-autofill, & .${outlinedInputClasses.input}:-webkit-autofill:hover, & .${outlinedInputClasses.input}:-webkit-autofill:focus`]:
              {
                WebkitTextFillColor: autofillTextColor,
                WebkitBoxShadow: `0 0 0 100px ${theme.palette.surface.bg} inset`,
                boxShadow: `0 0 0 100px ${theme.palette.surface.bg} inset`,
                transition: "background-color 5000s ease-in-out 0s",
                caretColor: theme.palette.text.primary,
              },
            [`& .${outlinedInputClasses.input}:-moz-autofill`]: {
              MozTextFillColor: autofillTextColor,
            },
            [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: "var(--TextField-brandBorderHoverColor)",
            },
            [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: "var(--TextField-brandBorderFocusedColor)",
            },
          };
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: "4px",
        },
      },
    },
  },
  typography: {
    fontFamily: "Inter, sans-serif",
    fontSize: 14,
    button: {
      fontSize: 14,
      fontWeight: 400,
    },
  },
});

export const muiCache = createCache({
  key: "mui",
  prepend: true,
});

interface IAppTheme {
  children?: React.ReactNode;
}

function AppTheme(props: IAppTheme) {
  return (
    <CacheProvider value={muiCache}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {props.children}
      </MuiThemeProvider>
    </CacheProvider>
  );
}

export default AppTheme;
